import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, GripVertical, Star, Power, Sparkles, Stethoscope, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';

const SERVICE_CATEGORIES = [
  { value: 'consultation', label: 'Consultation', icon: '💬' },
  { value: 'procedure', label: 'Procedure', icon: '🏥' },
  { value: 'lab_test', label: 'Lab Test', icon: '🔬' },
  { value: 'imaging', label: 'Imaging', icon: '📷' },
  { value: 'therapy', label: 'Therapy', icon: '🧘' },
  { value: 'preventive', label: 'Preventive', icon: '🛡️' },
  { value: 'specialty', label: 'Specialty', icon: '⭐' },
  { value: 'other', label: 'Other', icon: '📋' }
];

const PERIOD_TYPES = [
  { value: 'month', label: 'Per Month' },
  { value: 'year', label: 'Per Year' },
  { value: 'membership', label: 'Per Membership' }
];

const PlansTab = ({ plans, fetchData }) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [localPlans, setLocalPlans] = useState(plans);
  const [processingTemplate, setProcessingTemplate] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration_months: '',
    benefits: '',
    max_members: '1',
    plan_type: 'individual'
  });

  // Services state
  const [planServices, setPlanServices] = useState([]);
  const [newService, setNewService] = useState({
    name: '',
    category: 'consultation',
    quantity_per_period: 1,
    period_type: 'year',
    duration_minutes: 30,
    is_unlimited: false
  });

  // Update local plans when props change
  useEffect(() => {
    setLocalPlans(plans);
  }, [plans]);

  // Process template from localStorage when component mounts
  useEffect(() => {
    const processTemplate = async () => {
      const selectedTemplate = localStorage.getItem('selectedTemplate');
      if (!selectedTemplate) return;

      try {
        setProcessingTemplate(true);
        const template = JSON.parse(selectedTemplate);
        const token = localStorage.getItem('token');

        const plansToCreate = template.plans || [template];

        const createPromises = plansToCreate.map(async (plan) => {
          const durationMap = { monthly: 1, quarterly: 3, annual: 12 };
          const billingCycle = plan.billing_cycle || 'monthly';
          const duration_months = durationMap[billingCycle] || 1;

          const payload = {
            name: plan.name || plan.plan_name || 'Unnamed Plan',
            description: plan.description || plan.marketing_description || '',
            price: parseFloat(plan.price || plan.monthly_price || 0),
            duration_months: duration_months,
            benefits: plan.features || []
          };

          return axios.post(`${API}/plans`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        });

        await Promise.all(createPromises);
        
        toast.success(`Successfully created ${plansToCreate.length} plan${plansToCreate.length > 1 ? 's' : ''} from template!`);
        localStorage.removeItem('selectedTemplate');
        fetchData();
      } catch (error) {
        console.error('Error processing template:', error);
        toast.error(error.response?.data?.detail || 'Failed to create plans from template');
        localStorage.removeItem('selectedTemplate');
      } finally {
        setProcessingTemplate(false);
      }
    };

    processTemplate();
  }, [fetchData]);

  const handleAddService = () => {
    if (!newService.name.trim()) {
      toast.error('Service name is required');
      return;
    }

    setPlanServices([...planServices, {
      ...newService,
      id: Date.now(),
      quantity_per_period: newService.is_unlimited ? -1 : newService.quantity_per_period
    }]);

    setNewService({
      name: '',
      category: 'consultation',
      quantity_per_period: 1,
      period_type: 'year',
      duration_minutes: 30,
      is_unlimited: false
    });
  };

  const handleRemoveService = (serviceId) => {
    setPlanServices(planServices.filter(s => s.id !== serviceId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const benefits = formData.benefits.split('\n').filter(b => b.trim());
      
      // Prepare services for the API
      const services = planServices.filter(s => !s.isExisting).map(s => ({
        name: s.name,
        description: '',
        category: s.category,
        duration_minutes: s.duration_minutes,
        quantity_per_period: s.quantity_per_period,
        period_type: s.period_type
      }));

      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        duration_months: parseInt(formData.duration_months),
        benefits,
        max_members: parseInt(formData.max_members) || 1,
        plan_type: formData.plan_type || 'individual',
        services: editingPlan ? undefined : services // Only include services on create
      };

      if (editingPlan) {
        console.log('Updating plan:', editingPlan.id, 'with payload:', payload);
        const response = await axios.put(`${API}/plans/${editingPlan.id}`, payload);
        console.log('Update response:', response.data);
        
        // Add new services to existing plan
        if (services.length > 0) {
          await axios.post(`${API}/plans/${editingPlan.id}/services`, services);
        }
        
        toast.success('Plan updated successfully');
      } else {
        await axios.post(`${API}/plans`, payload);
        toast.success(`Plan created with ${services.length} services`);
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(editingPlan ? 'Failed to update plan' : 'Failed to create plan');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', price: '', duration_months: '', benefits: '', max_members: '1', plan_type: 'individual' });
    setPlanServices([]);
    setNewService({
      name: '',
      category: 'consultation',
      quantity_per_period: 1,
      period_type: 'year',
      duration_minutes: 30,
      is_unlimited: false
    });
    setEditingPlan(null);
  };

  const handleEdit = async (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      price: plan.price.toString(),
      duration_months: plan.duration_months.toString(),
      benefits: plan.benefits.join('\n'),
      max_members: (plan.max_members || 1).toString(),
      plan_type: plan.plan_type || 'individual'
    });

    // Load existing services for this plan
    try {
      const response = await axios.get(`${API}/plans/${plan.id}/services`);
      const existingServices = response.data.services || [];
      setPlanServices(existingServices.map(s => ({
        id: s.plan_service_id,
        name: s.name,
        category: s.category,
        quantity_per_period: s.quantity_per_period,
        period_type: s.period_type,
        duration_minutes: s.duration_minutes,
        is_unlimited: s.quantity_per_period === -1,
        isExisting: true
      })));
    } catch (error) {
      console.error('Error loading plan services:', error);
      setPlanServices([]);
    }

    setDialogOpen(true);
  };

  const handleDelete = async (planId, planName) => {
    if (!window.confirm(`Are you sure you want to delete "${planName}"? This will deactivate the plan.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/plans/${planId}`);
      toast.success('Plan deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(localPlans);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalPlans(items);

    try {
      const planOrders = items.map((plan, index) => ({
        id: plan.id,
        order: index
      }));

      await axios.put(`${API}/plans/reorder`, { plans: planOrders });
      toast.success('Plans reordered successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to save new order');
      setLocalPlans(plans);
    }
  };

  const handleToggleMostPopular = async (planId, currentStatus) => {
    try {
      await axios.patch(`${API}/plans/${planId}/most-popular`);
      toast.success(currentStatus ? 'Removed Most Popular tag' : 'Set as Most Popular');
      fetchData();
    } catch (error) {
      toast.error('Failed to update Most Popular status');
    }
  };

  const handleToggleStatus = async (planId, currentStatus) => {
    try {
      await axios.patch(`${API}/plans/${planId}/toggle-status`);
      const newStatus = !currentStatus;
      toast.success(newStatus ? 'Plan activated' : 'Plan deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle plan status');
    }
  };

  const getCategoryInfo = (category) => {
    return SERVICE_CATEGORIES.find(c => c.value === category) || SERVICE_CATEGORIES[7];
  };

  const formatQuantity = (qty, period) => {
    if (qty === -1) return 'Unlimited';
    const periodLabel = PERIOD_TYPES.find(p => p.value === period)?.label || period;
    return `${qty} ${periodLabel}`;
  };

  return (
    <div data-testid="plans-tab">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Membership Plans</h1>
          <p className="text-sm text-gray-500 mt-1">Drag and drop to reorder plans</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={() => navigate('/admin/templates')} 
            className="border-purple-300 hover:bg-purple-50 hover:border-purple-400"
          >
            <Sparkles className="h-4 w-4 mr-2 text-purple-600" />
            Browse Templates
          </Button>
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-plan-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Plan
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPlan ? 'Edit Membership Plan' : 'Create Membership Plan'}</DialogTitle>
              <DialogDescription>
                {editingPlan ? 'Update the membership plan details' : 'Add a new membership plan with included services'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Plan Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="plan-name-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required data-testid="plan-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price ($)</Label>
                  <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} required data-testid="plan-price-input" />
                </div>
                <div>
                  <Label>Duration (months)</Label>
                  <Input type="number" value={formData.duration_months} onChange={(e) => setFormData({...formData, duration_months: e.target.value})} required data-testid="plan-duration-input" />
                </div>
              </div>

              {/* Plan Type Section */}
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Tipo de Plan</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo de Membresía</Label>
                    <Select
                      value={formData.plan_type}
                      onValueChange={(value) => {
                        setFormData({
                          ...formData, 
                          plan_type: value,
                          max_members: value === 'individual' ? '1' : formData.max_members
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual (1 persona)</SelectItem>
                        <SelectItem value="family">Familiar (múltiples miembros)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Máximo de Miembros</Label>
                    <Input 
                      type="number" 
                      min="1" 
                      max="10"
                      value={formData.max_members} 
                      onChange={(e) => setFormData({...formData, max_members: e.target.value})} 
                      disabled={formData.plan_type === 'individual'}
                      data-testid="plan-max-members-input" 
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.plan_type === 'family' 
                        ? 'Incluye titular + dependientes' 
                        : 'Plan individual: solo 1 miembro'}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Benefits (one per line)</Label>
                <Textarea value={formData.benefits} onChange={(e) => setFormData({...formData, benefits: e.target.value})} placeholder="Benefit 1&#10;Benefit 2&#10;Benefit 3" required data-testid="plan-benefits-input" rows={3} />
              </div>

              {/* Services Section */}
              <div className="border-t pt-4 space-y-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Stethoscope className="h-4 w-4 text-teal-600" />
                    Included Services ({planServices.length})
                  </h3>
                  <p className="text-xs text-gray-500">Define services with quantities to track usage</p>
                </div>

                {/* Services List */}
                {planServices.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {planServices.map((service) => {
                      const cat = getCategoryInfo(service.category);
                      return (
                        <div key={service.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                          <div className="flex items-center gap-2">
                            <span>{cat.icon}</span>
                            <div>
                              <p className="font-medium">{service.name}</p>
                              <p className="text-xs text-gray-500">
                                {formatQuantity(service.quantity_per_period, service.period_type)} • {service.duration_minutes} min
                              </p>
                            </div>
                          </div>
                          {!service.isExisting ? (
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveService(service.id)} className="text-red-600 h-7 w-7 p-0">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Badge variant="secondary" className="text-xs">Saved</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add Service Form */}
                <div className="border-2 border-dashed border-teal-200 rounded-lg p-3 space-y-3 bg-teal-50/50">
                  <p className="text-sm font-medium text-teal-700">Add Service</p>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <Input
                        value={newService.name}
                        onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                        placeholder="Service name (e.g., Follow-up Consultation)"
                        className="text-sm"
                      />
                    </div>
                    
                    <Select value={newService.category} onValueChange={(v) => setNewService({ ...newService, category: v })}>
                      <SelectTrigger className="text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.icon} {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Input
                      type="number"
                      min="5"
                      value={newService.duration_minutes}
                      onChange={(e) => setNewService({ ...newService, duration_minutes: parseInt(e.target.value) || 30 })}
                      placeholder="Duration (min)"
                      className="text-sm"
                    />
                  </div>

                  <div className="flex items-center gap-3 p-2 bg-white rounded">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={newService.is_unlimited}
                        onCheckedChange={(v) => setNewService({ 
                          ...newService, 
                          is_unlimited: v,
                          quantity_per_period: v ? -1 : 1
                        })}
                      />
                      <span className="text-xs">Unlimited</span>
                    </div>

                    {!newService.is_unlimited && (
                      <>
                        <Input
                          type="number"
                          min="1"
                          value={newService.quantity_per_period}
                          onChange={(e) => setNewService({ ...newService, quantity_per_period: parseInt(e.target.value) || 1 })}
                          placeholder="Qty"
                          className="w-16 text-sm"
                        />
                        <Select value={newService.period_type} onValueChange={(v) => setNewService({ ...newService, period_type: v })}>
                          <SelectTrigger className="w-32 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PERIOD_TYPES.map(p => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                  </div>

                  <Button type="button" onClick={handleAddService} variant="outline" size="sm" className="w-full border-teal-300 text-teal-700 hover:bg-teal-100">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Service
                  </Button>
                </div>
              </div>

              <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" data-testid="plan-submit-btn">
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="plans">
          {(provided) => (
            <div 
              {...provided.droppableProps} 
              ref={provided.innerRef}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {localPlans.map((plan, index) => (
                <Draggable key={plan.id} draggableId={plan.id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={snapshot.isDragging ? 'opacity-50' : ''}
                    >
                      <Card className={`border-2 hover:shadow-xl transition-all duration-200 relative overflow-hidden ${plan.is_most_popular ? 'border-yellow-400 border-4' : 'hover:border-teal-500'}`} data-testid={`plan-card-${plan.id}`}>
                        {/* Status & Most Popular Badges */}
                        <div className="absolute top-4 right-4 z-10 flex gap-2">
                          <Badge 
                            className={`font-bold px-3 py-1 text-xs cursor-pointer ${
                              plan.is_active 
                                ? 'bg-green-500 hover:bg-green-600 text-white' 
                                : 'bg-gray-400 hover:bg-gray-500 text-white'
                            }`}
                            onClick={() => handleToggleStatus(plan.id, plan.is_active)}
                          >
                            <Power className="h-3 w-3 mr-1" />
                            {plan.is_active ? 'ACTIVE' : 'DRAFT'}
                          </Badge>
                          {plan.plan_type === 'family' && (
                            <Badge className="bg-blue-500 text-white font-bold px-3 py-1 text-xs">
                              👨‍👩‍👧‍👦 {plan.max_members || 4} miembros
                            </Badge>
                          )}
                          {plan.is_most_popular && (
                            <Badge className="bg-yellow-400 text-yellow-900 font-bold px-3 py-1 text-xs flex items-center gap-1">
                              <Star className="h-3 w-3 fill-current" />
                              MOST POPULAR
                            </Badge>
                          )}
                        </div>
                        
                        {/* Drag Handle */}
                        <div 
                          {...provided.dragHandleProps}
                          className="absolute top-4 left-4 cursor-grab active:cursor-grabbing z-10"
                        >
                          <GripVertical className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        </div>

                        <div className="absolute top-0 right-0 w-32 h-32 bg-teal-50 rounded-full -mr-16 -mt-16 opacity-50"></div>
                        
                        <CardHeader className="relative pt-12">
                          <CardTitle className="text-xl">{plan.name}</CardTitle>
                          <CardDescription className="text-base">{plan.description}</CardDescription>
                        </CardHeader>
                        
                        <CardContent className="relative">
                          <div className="space-y-4">
                            <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-lg">
                              <p className="text-4xl font-bold text-teal-700">${plan.price}</p>
                              <p className="text-sm text-teal-600 font-medium mt-1">per {plan.duration_months} month(s)</p>
                              <p className="text-xs text-orange-600 font-medium mt-2 bg-orange-50 inline-block px-2 py-1 rounded">+ $25 enrollment fee (first time only)</p>
                            </div>
                            
                            <div>
                              <p className="text-sm font-semibold text-gray-800 mb-3">Benefits:</p>
                              <ul className="text-sm text-gray-700 space-y-2 max-h-48 overflow-y-auto">
                                {plan.benefits.map((benefit, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="text-teal-600 mr-2 font-bold">✓</span>
                                    <span className="flex-1">{benefit}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div className="flex flex-col gap-2 pt-4 border-t">
                              <Button
                                variant={plan.is_most_popular ? "default" : "outline"}
                                size="sm"
                                className={`w-full ${plan.is_most_popular ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'hover:bg-yellow-50 border-yellow-400 text-yellow-700'}`}
                                onClick={() => handleToggleMostPopular(plan.id, plan.is_most_popular)}
                              >
                                <Star className={`h-4 w-4 mr-1 ${plan.is_most_popular ? 'fill-current' : ''}`} />
                                {plan.is_most_popular ? 'Most Popular' : 'Mark as Popular'}
                              </Button>
                              
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 hover:bg-teal-50"
                                  onClick={() => handleEdit(plan)}
                                  data-testid={`edit-plan-${plan.id}`}
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => handleDelete(plan.id, plan.name)}
                                  data-testid={`delete-plan-${plan.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};


export default PlansTab;
